import { FC, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { PublicOrderFormConfig } from "../../../types/PublicOrderFormConfig";
import PublicFormConfig from "../../../models/PublicFormConfig";
import SuccessToast from "../../../components/SuccessToast/SuccessToast";
import ValidationToast from "../../../components/ValidationToast/ValidationToast";
import { useWorkspaceUsers } from "../../../hooks";
import { useWorkspaceData } from "@data";
import { WorkspaceRole } from "../../../types";
import { useNavigate } from "react-router-dom";
import { Button, TabButton, InfoMessageMolecule } from "@components";
import { SettingsRowProps } from "../components/SettingsRow";
import GeneralTab from "./order-form/GeneralTab";
import TextsTab from "./order-form/TextsTab";
import AppearanceTab from "./order-form/AppearanceTab";
import ProductSettingsTab from "./order-form/ProductSettingsTab";

const buildDefaultsFromModel = (): PublicOrderFormConfig => {
  const modelDefaults = PublicFormConfig.default().data();
  return {
    allowCustomOrderFlow: modelDefaults.allowCustomOrderFlow,
    allowProductListOrderFlow: modelDefaults.allowProductListOrderFlow,
    language: modelDefaults.language,
    productListFlowTitle: modelDefaults.productListFlowTitle,
    productListFlowDescription: modelDefaults.productListFlowDescription,
    productListFlowConfirmationTitle:
      modelDefaults.productListFlowConfirmationTitle,
    productListFlowConfirmationDescription:
      modelDefaults.productListFlowConfirmationDescription,
    productListFlowConfirmationNextSteps:
      modelDefaults.productListFlowConfirmationNextSteps,
    customOrderFlowTitle: modelDefaults.customOrderFlowTitle,
    customOrderFlowDescription: modelDefaults.customOrderFlowDescription,
    customOrderFlowConfirmationTitle:
      modelDefaults.customOrderFlowConfirmationTitle,
    customOrderFlowConfirmationDescription:
      modelDefaults.customOrderFlowConfirmationDescription,
    customOrderFlowConfirmationNextSteps:
      modelDefaults.customOrderFlowConfirmationNextSteps,
    primaryColor: modelDefaults.primaryColor,
    primaryColorLight: modelDefaults.primaryColorLight,
    primaryColorDark: modelDefaults.primaryColorDark,
    headerColor: modelDefaults.headerColor,
    logoUrl: modelDefaults.logoUrl,
    logoRedirectUrl: modelDefaults.logoRedirectUrl,
    defaultPickupLocation: modelDefaults.defaultPickupLocation,
    savedUrl: modelDefaults.savedUrl,
    showPricesWithVat: modelDefaults.showPricesWithVat,
    enableCategoryFilter: modelDefaults.enableCategoryFilter,
    categoryFilters: modelDefaults.categoryFilters,
    productOrder: modelDefaults.productOrder,
  };
};

interface OrderFormSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
  currentSubtab?: string;
  onSubtabChange?: (subtab: string) => void;
}

const OrderFormSettingsContent: FC<OrderFormSettingsContentProps> = ({
  SettingsRow,
  currentSubtab = "general",
  onSubtabChange,
}) => {
  const { t } = useTranslation(["common", "order_form"]);
  const { activeWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<PublicOrderFormConfig>(
    buildDefaultsFromModel(),
  );
  const [currentConfigId, setCurrentConfigId] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  // URL generation state
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  // Validation state
  const [isUrlSlugValid, setIsUrlSlugValid] = useState(true);

  // Use subtab from props instead of managing local state
  const activeTab = currentSubtab;

  // Fetch workspace users to check for OrderDraft API keys
  const workspaceUsersQuery = useWorkspaceUsers();

  // Check if OrderDraft API key exists
  const hasOrderDraftApiKey = useMemo(() => {
    if (!workspaceUsersQuery.data) return false;

    return workspaceUsersQuery.data.some(
      (user) =>
        user.isApiKey === true && user.role === WorkspaceRole.OrderDraft,
    );
  }, [workspaceUsersQuery.data]);

  // Determine what to show based on API key and URL status
  const shouldShowApiKeyRequired = useMemo(() => {
    return !hasOrderDraftApiKey && !workspaceUsersQuery.isLoading;
  }, [hasOrderDraftApiKey, workspaceUsersQuery.isLoading]);

  const shouldShowCredentialsInput = useMemo(() => {
    return (
      hasOrderDraftApiKey &&
      !Boolean(config.savedUrl) &&
      !workspaceUsersQuery.isLoading
    );
  }, [hasOrderDraftApiKey, config.savedUrl, workspaceUsersQuery.isLoading]);

  const shouldShowConfig = useMemo(() => {
    return (
      hasOrderDraftApiKey &&
      Boolean(config.savedUrl) &&
      !workspaceUsersQuery.isLoading
    );
  }, [hasOrderDraftApiKey, config.savedUrl, workspaceUsersQuery.isLoading]);

  // Workspace locations come from the centrally-loaded data store.
  const { locations } = useWorkspaceData();

  // Load configuration from API on component mount
  useEffect(() => {
    const loadConfig = async () => {
      if (!activeWorkspace?.workspaceId) return;

      try {
        const loadedConfig = await PublicFormConfig.getByWorkspaceId(
          activeWorkspace.workspaceId,
        );
        if (loadedConfig) {
          setConfig(loadedConfig as PublicOrderFormConfig);
          setCurrentConfigId(loadedConfig.id || null);
          setLogoUrl(loadedConfig.logoUrl || "");
          // Also set credentials state if they exist in the loaded config
          if ((loadedConfig as any).clientId)
            setClientId((loadedConfig as any).clientId);
          if ((loadedConfig as any).clientSecret)
            setClientSecret((loadedConfig as any).clientSecret);
        }
      } catch (error) {
        console.error("Failed to load order form config:", error);
      }
    };

    loadConfig();
  }, [activeWorkspace?.workspaceId]);

  // Generic handler for updating any config field
  const handleConfigChange = <K extends keyof PublicOrderFormConfig>(
    key: K,
    value: PublicOrderFormConfig[K],
  ) => {
    setConfig((prev) => ({ ...prev, [key]: value }));

    // Update logo URL state if logo is changed
    if (key === "logoUrl" && typeof value === "string") {
      setLogoUrl(value);
    }
  };

  // Save configuration to API
  const handleSaveConfig = async (
    configOverride?: Partial<PublicOrderFormConfig>,
  ) => {
    if (!activeWorkspace?.workspaceId) return;

    setIsSaving(true);
    try {
      let configToSave = {
        ...config,
        clientId: clientId || config.clientId,
        clientSecret: clientSecret || config.clientSecret,
        ...configOverride,
      };

      // Only generate URL if we have credentials (not a delete operation)
      if (configToSave.clientId && configToSave.clientSecret) {
        // Determine the correct savedUrl based on the toggle
        if (configToSave.useCustomUrlSlug && configToSave.urlSlug) {
          // Use the custom slug
          const path = `/order/${configToSave.urlSlug}`;
          configToSave.savedUrl = toAbsoluteUrl(path);
        } else {
          // If custom slug is disabled, clear the slug value to free it up for others
          configToSave.urlSlug = "";
          // Use the default, long URL
          const path = `/order/${activeWorkspace.workspaceId}?clientId=${encodeURIComponent(configToSave.clientId)}&clientSecret=${encodeURIComponent(configToSave.clientSecret)}`;
          configToSave.savedUrl = toAbsoluteUrl(path);
        }
      }

      if (currentConfigId) {
        // Update existing config
        const existingConfig = new PublicFormConfig({
          ...configToSave,
          id: currentConfigId,
          workspaceId: activeWorkspace.workspaceId,
        });
        await PublicFormConfig.update(existingConfig, configToSave);
      } else {
        // Create new config
        const success = await PublicFormConfig.create(
          activeWorkspace.workspaceId,
          {
            ...configToSave,
            workspaceId: activeWorkspace.workspaceId,
          },
        );
        if (success) {
          // Reload to get the ID
          const newConfig = await PublicFormConfig.getByWorkspaceId(
            activeWorkspace.workspaceId,
          );
          if (newConfig && newConfig.id) {
            setCurrentConfigId(newConfig.id);
          }
        }
      }

      // After saving, update the local state to reflect the changes immediately
      setConfig(configToSave);

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (error) {
      console.error("Failed to save order form config:", error);
      setErrorMessage("Failed to save configuration. Please try again.");
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Tab changes are handled by parent component through onSubtabChange

  // Simple URL generation using current origin
  const toAbsoluteUrl = (url: string): string => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `${window.location.origin}/${url.replace(/^\/+/, "")}`;
  };

  const handleGenerateUrl = async () => {
    if (!clientId.trim() || !clientSecret.trim()) return;
    if (!activeWorkspace?.workspaceId) return;

    const path = `/order/${activeWorkspace.workspaceId}?clientId=${encodeURIComponent(clientId.trim())}&clientSecret=${encodeURIComponent(clientSecret.trim())}`;
    const link = toAbsoluteUrl(path);

    // Update local state
    handleConfigChange("savedUrl", link);

    // Automatically save to Firebase
    try {
      await handleSaveConfig({ savedUrl: link });
    } catch (error) {
      console.error("Error saving URL to config:", error);
    }

    // Copy to clipboard
    navigator.clipboard.writeText(link).catch(() => {});
  };

  // Tab buttons
  const tabButtons = (
    <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
      <TabButton
        label={t("order_form:tabs.general_tab")}
        isSelected={activeTab === "general"}
        onClick={() => onSubtabChange?.("general")}
      />
      <TabButton
        label={t("order_form:tabs.texts_tab")}
        isSelected={activeTab === "texts"}
        onClick={() => onSubtabChange?.("texts")}
      />
      <TabButton
        label={t("order_form:tabs.appearance_tab")}
        isSelected={activeTab === "appearance"}
        onClick={() => onSubtabChange?.("appearance")}
      />
      <TabButton
        label={t("order_form:tabs.products_tab")}
        isSelected={activeTab === "products"}
        onClick={() => onSubtabChange?.("products")}
      />
    </ul>
  );

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex-1 sm:max-w-2xl">
          <h2 className="text-xl font-semibold text-text sm:text-2xl mb-2">
            {t("order_form:title")}
          </h2>
          <p className="text-secondary text-base dark:text-secondary">
            {t("order_form:description")}
          </p>
        </div>
        {shouldShowConfig && (
          <Button
            variant="primary"
            onClick={handleSaveConfig}
            disabled={isSaving || !isUrlSlugValid}
            size="sm"
            className="mt-2 sm:mt-0 flex-shrink-0 whitespace-nowrap"
          >
            {isSaving
              ? t("order_form:save.saving")
              : t("order_form:save.button")}
          </Button>
        )}
      </div>

      {/* Success Toast */}
      <SuccessToast
        show={showSuccessToast}
        title={t("common:success", "Vellykket")}
        message={t(
          "order_form:messages.config_saved",
          "Konfigurasjonen ble lagret",
        )}
        onClose={() => setShowSuccessToast(false)}
      />

      {/* Error Toast */}
      <ValidationToast
        show={showErrorToast}
        title={t("common:error", "Error")}
        message={errorMessage}
        onClose={() => setShowErrorToast(false)}
      />

      {/* Step 1: Show API key required message */}
      {shouldShowApiKeyRequired && (
        <InfoMessageMolecule
          title={t("order_form:api_key_required.title")}
          description={t("order_form:api_key_required.description")}
          buttonText={t("order_form:api_key_required.button")}
          onButtonClick={() => navigate("/settings?tab=api-keys")}
        />
      )}

      {/* Step 2: Show credentials input when API key exists but no URL generated */}
      {shouldShowCredentialsInput && (
        <InfoMessageMolecule
          title={t("order_form:form_url.title")}
          description={t("order_form:form_url.description")}
          buttonText={t("order_form:form_url.get_link_button", "Generer URL")}
          onButtonClick={handleGenerateUrl}
          buttonDisabled={!clientId.trim() || !clientSecret.trim()}
        >
          <div className="mb-4">
            <h4 className="text-sm font-medium text-brand mb-3">
              {t("order_form:form_url.api_credentials", "API-legitimasjon")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-lgb-blue-600 dark:text-brand mb-1">
                  {t("order_form:form_url.client_id_label", "Client ID")}
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder={t(
                    "order_form:form_url.client_id_placeholder",
                    "Din Client ID",
                  )}
                  autoComplete="off"
                  className="w-full px-3 py-2 text-sm border border-brand bg-surface-input rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-brand dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-lgb-blue-600 dark:text-brand mb-1">
                  {t(
                    "order_form:form_url.client_secret_label",
                    "Client Secret",
                  )}
                </label>
                <input
                  type="text"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={t(
                    "order_form:form_url.client_secret_placeholder",
                    "Din Client Secret",
                  )}
                  autoComplete="off"
                  className="w-full px-3 py-2 text-sm border border-brand bg-surface-input rounded-md focus:border-brand focus:ring-1 focus:ring-brand dark:border-brand dark:text-white"
                />
              </div>
            </div>
          </div>
        </InfoMessageMolecule>
      )}

      {/* Step 3: Show configuration form when both API key exists and URL is generated */}
      {shouldShowConfig && (
        <>
          <div className="mb-6">{tabButtons}</div>
          {activeTab === "general" && (
            <GeneralTab
              SettingsRow={SettingsRow}
              config={config}
              logoUrl={logoUrl}
              locations={locations}
              handleConfigChange={handleConfigChange}
              onSaveConfig={handleSaveConfig}
              clientId={clientId}
              clientSecret={clientSecret}
              setClientId={setClientId}
              setClientSecret={setClientSecret}
              currentConfigId={currentConfigId}
              onValidationChange={setIsUrlSlugValid}
            />
          )}

          {activeTab === "texts" && (
            <TextsTab
              SettingsRow={SettingsRow}
              config={config}
              handleConfigChange={handleConfigChange}
            />
          )}
          {activeTab === "appearance" && (
            <AppearanceTab
              SettingsRow={SettingsRow}
              config={config}
              handleConfigChange={handleConfigChange}
            />
          )}
          {activeTab === "products" && (
            <ProductSettingsTab
              SettingsRow={SettingsRow}
              config={config}
              handleConfigChange={handleConfigChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrderFormSettingsContent;
